/**
 * Главный экран: баланс, быстрый запуск сделки, плитки разделов.
 * Визуал по design-spec §7 (Home): hero-карточка, mission-карточка, три плитки.
 * Наполнение — наша воронка (BOT_SPEC ШАГ 3, §4.4).
 */
import { useEffect, useState } from 'react';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type { Stats } from '../lib/api.js';
import { haptic, openLink } from '../lib/telegram.js';
import { useApp } from '../state/app.js';
import { money } from '../lib/format.js';
import { useCountUp } from '../lib/useCountUp.js';

export function HomeScreen({ brandName }: { brandName: string }) {
  const { me, refresh, setTab, showToast, art, t } = useApp();
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    void api.stats().then(setStats).catch(() => undefined);
  }, []);

  // «докручивающиеся» цифры: баланс и токены доезжают до значения плавно
  const demoBalance = useCountUp(me?.balances.demo ?? 0);
  const realBalance = useCountUp(me?.balances.real ?? 0);
  const tokens = Math.round(useCountUp(me?.user.tokens ?? 0, 500));

  if (!me) return null;

  const go = (tab: Parameters<typeof setTab>[0]) => () => {
    haptic();
    setTab(tab);
  };

  /**
   * Баннер «переключись на Real»: тот же гейт, что и кнопка в аккаунте.
   * Отказ «нужен депозит» сразу открывает ссылку пополнения — человек не
   * ищет кнопку по экранам; «нет аккаунта» уводит на карточку привязки.
   */
  async function goReal() {
    haptic('medium');
    try {
      await api.setMode('REAL');
      await refresh();
      showToast(t('app.account.real_on'));
    } catch (err) {
      const code =
        err instanceof ApiError && err.payload && typeof err.payload === 'object'
          ? String((err.payload as { error?: unknown }).error ?? '')
          : '';
      showToast(apiMessage(err, t, 'app.account.save_failed'));
      if (code === 'deposit_required') {
        try {
          const { link } = await api.depositLink();
          openLink(link);
        } catch {
          setTab('tokens');
        }
        return;
      }
      if (code === 'account_required' || code === 'foreign_account') setTab('account');
    }
  }

  return (
    <div className="screen">
      {/* hero: режим, баланс, токены */}
      <section className="card card-lg home-hero">
        <img className="home-hero-art" src={art('art.hero', '/art/hero.webp')} alt="" loading="eager" />
        <div className="row-between">
          <span className="kicker">{t('app.home.mode_label')}</span>
          <span className={`pill ${me.user.mode === 'REAL' ? 'pill-green' : 'pill-pink'}`}>
            <span className="dot-pulse" />
            {me.user.mode}
          </span>
        </div>

        <h1 className="home-hero-title">
          <span className="grad-text">{brandName}</span>
        </h1>
        <p className="body">{t('app.home.subtitle')}</p>

        <div className="home-balance">
          <div>
            <div className="label">{t('app.home.demo_balance')}</div>
            <div className="home-balance-value">${money(demoBalance)}</div>
          </div>
          <div>
            <div className="label">{t('app.home.stat_tokens')}</div>
            <div className="home-balance-value home-balance-tokens">{tokens} 💎</div>
          </div>
        </div>

        <button type="button" className="cta cta-launch cta-block" onClick={go('trade')}>
          {t('app.home.run')}
        </button>
      </section>

      {/* баннер «открой реальный счёт» — только на демо, в REAL пропадает */}
      {me.user.mode !== 'REAL' && (
        <section className="card card-tinted">
          <span className="kicker">{t('app.home.go_real_title')}</span>
          <p className="body">{t('app.home.go_real_body')}</p>
          <button type="button" className="cta cta-block" onClick={() => void goReal()}>
            {t('app.account.go_real')}
          </button>
        </section>
      )}

      {/* реальный баланс / депозит */}
      <section className="card card-tinted">
        <span className="kicker">{t('app.home.real_account')}</span>
        <div className="row-between">
          <div className="home-real-value">
            ${money(realBalance)} <span className="home-real-currency">{me.balances.currency}</span>
          </div>
          <button type="button" className="cta cta-gold cta-sm" onClick={go('tokens')}>
            {t('app.home.deposit')}
          </button>
        </div>
        <p className="body">
          {me.broker.linked
            ? t('app.home.broker_linked')
            : t('app.home.broker_missing')}
        </p>
      </section>

      {/* статистика */}
      <section className="stat-grid">
        <div className="stat-tile">
          <div className="stat-value" style={{ '--stat-color': 'var(--violet)' } as React.CSSProperties}>
            {stats?.total ?? 0}
          </div>
          <div className="stat-label">{t('app.home.stat_trades')}</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value" style={{ '--stat-color': 'var(--green)' } as React.CSSProperties}>
            {stats?.winRate ?? 0}%
          </div>
          <div className="stat-label">{t('app.home.stat_winrate')}</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value" style={{ '--stat-color': 'var(--gold)' } as React.CSSProperties}>
            {stats?.tokensSpent ?? 0}
          </div>
          <div className="stat-label">{t('app.home.stat_tokens')}</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value" style={{ '--stat-color': 'var(--pink)' } as React.CSSProperties}>
            {stats?.bestStreak ?? 0}
          </div>
          <div className="stat-label">{t('app.home.stat_streak')}</div>
        </div>
      </section>

      {/* любимая пара показывается только когда сделки уже были */}
      {stats?.favouritePair && (
        <section className="favourite-pair">
          <span className="favourite-pair-label">{t('app.home.favourite_pair')}</span>
          <span className="favourite-pair-value">{stats.favouritePair}</span>
        </section>
      )}

      {/* плитки разделов */}
      <section className="tile-grid">
        <button type="button" className="tile" onClick={go('trade')}>
          <span className="tile-icon">⚡</span>
          <span className="tile-title">{t('app.home.tile_trade_title')}</span>
          <span className="tile-text">{t('app.home.tile_trade_text')}</span>
        </button>
        <button type="button" className="tile" onClick={go('tokens')}>
          <span className="tile-icon">
            <img src={art('art.pack_basic', '/art/pack-basic.webp')} alt="" width={26} height={26} />
          </span>
          <span className="tile-title">{t('app.nav.tokens')}</span>
          <span className="tile-text">{t('app.home.tile_tokens_text')}</span>
        </button>
        <button type="button" className="tile" onClick={go('account')}>
          <span className="tile-icon">👤</span>
          <span className="tile-title">{t('app.nav.account')}</span>
          <span className="tile-text">{t('app.home.tile_account_text')}</span>
        </button>
      </section>
    </div>
  );
}
